import { useCallback, useEffect, useRef, useState } from "react";

export interface HoveredSlice {
    bucketIndex: number;
    /** Null for a column with no activity, which is hovered as a whole. */
    seriesId: string | null;
}

interface PendingSlice {
    slice: HoveredSlice | null;
    timer: ReturnType<typeof setTimeout>;
}

export const OPEN_DELAY_MS = 150;

export const isSameSlice = (a: HoveredSlice | null, b: HoveredSlice | null) =>
    a?.bucketIndex === b?.bucketIndex && a?.seriesId === b?.seriesId;

/**
 * Which segment's, or empty column's, tooltip is open. It opens after a short
 * hover and closes at once when the pointer leaves. Once open, `hoverSlice`
 * switches to any other segment or column at once.
 */
export function useSliceHover() {
    const [openSlice, setOpenSlice] = useState<HoveredSlice | null>(null);
    const openSliceRef = useRef<HoveredSlice | null>(null);
    const pending = useRef<PendingSlice | null>(null);

    const cancelPending = useCallback(() => {
        if (pending.current) {
            clearTimeout(pending.current.timer);
            pending.current = null;
        }
    }, []);
    const show = useCallback(
        (slice: HoveredSlice | null) => {
            cancelPending();
            openSliceRef.current = slice;
            setOpenSlice(slice);
        },
        [cancelPending],
    );
    const schedule = useCallback(
        (slice: HoveredSlice | null, delay: number) => {
            if (pending.current && isSameSlice(pending.current.slice, slice)) {
                return;
            }
            cancelPending();
            pending.current = {
                slice,
                timer: setTimeout(() => show(slice), delay),
            };
        },
        [cancelPending, show],
    );

    const leave = useCallback(() => {
        if (openSliceRef.current === null) {
            cancelPending();
        } else {
            show(null);
        }
    }, [cancelPending, show]);
    const hoverSlice = useCallback(
        (slice: HoveredSlice | null) => {
            const current = openSliceRef.current;
            if (isSameSlice(slice, current)) {
                cancelPending();
            } else if (slice === null) {
                leave();
            } else if (current === null) {
                schedule(slice, OPEN_DELAY_MS);
            } else {
                show(slice);
            }
        },
        [cancelPending, leave, schedule, show],
    );
    const close = useCallback(() => show(null), [show]);

    useEffect(() => cancelPending, [cancelPending]);

    return { openSlice, hoverSlice, release: leave, close };
}
